import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MapPin, Clock, CalendarPlus, ArrowRight } from "lucide-react";
import PageHero from "../components/PageHero";
import { Reveal } from "../components/motion";

function gcalUrl(e) {
  const d = e.date.replace(/-/g, "");
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: e.title,
    dates: `${d}/${d}`,
    details: e.desc || "",
    location: e.place || "",
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export default function Events() {
  const { t } = useTranslation();
  const events = t("home.events.items", { returnObjects: true });
  const list = Array.isArray(events) ? events : [];

  return (
    <div data-testid="page-events">
      <PageHero
        testId="events-hero"
        kicker={t("events.kicker")}
        title={t("events.headline")}
        lead={t("events.lead")}
        aside={
          <div className="paper p-8 lg:mt-24 flex items-center gap-5" data-testid="events-count-card">
            <span className="font-display text-6xl text-forest leading-none">{list.length}</span>
            <div>
              <div className="font-semibold text-ink">{t("events.upcoming_count")}</div>
              <div className="text-xs text-slate mt-1">{t("events.note")}</div>
            </div>
          </div>
        }
      />

      <section className="surface-2" data-testid="events-list-section">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-16 lg:py-24">
          {list.length === 0 ? (
            <div className="text-center text-slate py-20 font-display text-2xl" data-testid="events-empty">{t("events.empty")}</div>
          ) : (
            <ul className="space-y-4" data-testid="events-list">
              {list.map((e, i) => (
                <Reveal key={i} delay={Math.min(i, 5) * 0.06}>
                  <li className="gxr-card group p-6 lg:p-7 flex flex-col md:flex-row md:items-center gap-6" data-testid={`event-item-${i + 1}`}>
                    <span className="h-20 w-20 rounded-xl flex-shrink-0 flex flex-col items-center justify-center bg-ivory border border-line text-forest transition-colors group-hover:bg-forest group-hover:text-white group-hover:border-forest">
                      <span className="font-display text-3xl leading-none">{e.day}</span>
                      <span className="text-[11px] uppercase tracking-wide mt-1 opacity-80">{e.month}</span>
                    </span>
                    <div className="flex-1 min-w-0">
                      <span className="pill">{e.type}</span>
                      <h3 className="font-display text-xl lg:text-2xl text-ink mt-2 leading-tight group-hover:text-forest transition-colors">{e.title}</h3>
                      <p className="mt-2 text-sm text-slate leading-relaxed">{e.desc}</p>
                      <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-slate">
                        <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-gold" />{e.place}</span>
                        <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-gold" />{e.time}</span>
                      </div>
                    </div>
                    <div className="flex flex-row md:flex-col gap-2 flex-shrink-0">
                      <a
                        href={gcalUrl(e)}
                        target="_blank"
                        rel="noreferrer noopener"
                        data-testid={`event-calendar-${i + 1}`}
                        className="btn-gold-outline !py-2 !px-4 !text-[10px]"
                      >
                        <CalendarPlus className="h-3.5 w-3.5" /> {t("events.add_calendar")}
                      </a>
                      <Link to="/contact" data-testid={`event-register-${i + 1}`} className="btn-forest !py-2 !px-4 !text-[10px]">
                        {t("events.register")} <ArrowRight className="h-3.5 w-3.5 rtl:rotate-180" />
                      </Link>
                    </div>
                  </li>
                </Reveal>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
